import { useQuery } from '@tanstack/react-query';
import { getAllRolesQuery } from '../../../../api/users-api';
import { Link, Outlet, useParams } from 'react-router-dom';
import AvatarChip from '../../../../shared/avatar-chip';
import {
  pagesConfig,
  roleManagerDetailPages,
} from '../../../../shared/pages-config';
import { useState } from 'react';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import DeleteRoleDialog from '../delete-role-dialog';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { gPanelTiers } from '../../../../shared/gPanel-tiers';
import RoleDetailsLayout from '../role-details-layout';
import { useRouteMatch } from '../../../../shared/useRouteMatch';
import SimpleBreadcrumbsPage from '../../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { Messages } from '../../../../utils/message-constants';

import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Delete from '@mui/icons-material/Delete';

export default function RoleDetailsPage() {
  const {
    changeLogRoles,
    administrationRoles,
    usersTier,
    hasRoleWithLimitedVisibility,
    isDefault,
  } = useUserSettings();
  const [openDeleteRole, setOpenDeleteRole] = useState(false);
  const { roleName } = useParams() as { roleName: string };
  const { data: roles, isLoading } = useQuery(getAllRolesQuery());
  const currentRole = roles?.find((role) => role.roleName === roleName);

  const routeMatch = useRouteMatch([
    `${pagesConfig.roleManager.link}/${pagesConfig.roleManagerDetails.link}/${pagesConfig.roleManagerPermissions.link}`,
    `${pagesConfig.roleManager.link}/${pagesConfig.roleManagerDetails.link}/${pagesConfig.roleManagerSettings.link}`,
    `${pagesConfig.roleManager.link}/${pagesConfig.roleManagerDetails.link}/${pagesConfig.roleManagerMembership.link}`,
    `${pagesConfig.roleManager.link}/${pagesConfig.roleManagerDetails.link}/${pagesConfig.roleManagerLogs.link}`,
    `${pagesConfig.roleManager.link}/${pagesConfig.roleManagerDetails.link}/${pagesConfig.roleManagerDirectorySearch.link}`,
    `${pagesConfig.roleManager.link}/${pagesConfig.roleManagerDetails.link}/${pagesConfig.roleManagerLimitedVisibility.link}`,
  ]);
  const currentTab =
    routeMatch?.pattern?.path ?? `${pagesConfig.roleManagerPermissions.link}`;
  const roleNames = roles?.map((role) => role.roleName) || [];
  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.administration.name },
        {
          label: pagesConfig.roleManager.name,
          path: pagesConfig.roleManager.link,
        },
        { label: pagesConfig.roleManagerDetails.name },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Card sx={{ p: 1, px: 2 }}>
          <Stack
            flexDirection={{ md: 'column', lg: 'row' }}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack gap={0.5}>
              {isLoading && !currentRole ? (
                <Skeleton variant="rounded" width={200} height={30} />
              ) : (
                <Typography sx={{ fontWeight: 400, fontSize: 24, mb: -1 }}>
                  {currentRole?.roleName ? currentRole.roleName : ' '}
                </Typography>
              )}
              {isLoading || !currentRole ? (
                <Skeleton variant="rounded" width={110} height={14} />
              ) : (
                <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
                  Role Type:{' '}
                  {currentRole?.roleType
                    ? currentRole.roleType.charAt(0).toUpperCase() +
                      currentRole.roleType.slice(1)
                    : ' '}
                </Typography>
              )}
            </Stack>
            <Stack spacing={1}>
              {isLoading || !currentRole ? (
                <Skeleton variant="rounded" width={300} height={20} />
              ) : currentRole.lastUpdated ? (
                <Typography
                  textTransform={'capitalize'}
                  sx={{ fontWeight: 300, fontSize: 14 }}
                >
                  {`Last Updated On: ${
                    currentRole?.lastUpdated
                      ? new Date(currentRole.lastUpdated).toLocaleString()
                      : ''
                  }`}
                </Typography>
              ) : null}
              {isLoading || !currentRole ? (
                <Skeleton variant="rounded" width={300} height={20} />
              ) : currentRole.lastUpdatingUser ? (
                <Stack flexDirection={'row'} gap={1} alignItems={'center'}>
                  <Typography
                    textTransform={'capitalize'}
                    sx={{ fontWeight: 300, fontSize: 14 }}
                  >
                    Last Updated By:
                  </Typography>
                  <AvatarChip
                    email={currentRole?.lastUpdatingUser || ''}
                    type="User"
                  />
                </Stack>
              ) : null}
            </Stack>
            <Stack spacing={1}>
              {isLoading || !currentRole ? (
                <Skeleton variant="rounded" width={230} height={20} />
              ) : currentRole.createdDate ? (
                <Typography sx={{ fontWeight: 300, fontSize: 14 }}>
                  {`Created On: 
                  ${
                    currentRole?.createdDate
                      ? new Date(currentRole.createdDate).toLocaleString()
                      : ' '
                  }`}
                </Typography>
              ) : null}
              {isLoading || !currentRole ? (
                <Skeleton variant="rounded" width={300} height={20} />
              ) : currentRole.creatorEmail && !currentRole.default ? (
                <Stack flexDirection={'row'} gap={1}>
                  <Typography
                    textTransform={'capitalize'}
                    sx={{ fontWeight: 300, fontSize: 14 }}
                  >
                    Created By:
                  </Typography>
                  <AvatarChip
                    email={currentRole?.creatorEmail || ''}
                    type="User"
                  />
                </Stack>
              ) : null}
            </Stack>
            <Stack direction={'row'}>
              <Tooltip
                arrow
                title={
                  currentRole?.default
                    ? 'Default roles cannot be deleted.'
                    : !isAbleToSeeUIElement(
                        administrationRoles,
                        AdministrationRoles.DeleteRole.value
                      )
                    ? `You are missing the ${AdministrationRoles.DeleteRole.name} permission to perform this action.`
                    : 'Delete'
                }
              >
                <span>
                  <IconButton
                    onClick={() => setOpenDeleteRole(true)}
                    disabled={
                      !isAbleToSeeUIElement(
                        administrationRoles,
                        AdministrationRoles.DeleteRole.value
                      ) ||
                      !currentRole ||
                      currentRole.default
                    }
                  >
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Card>

        <Card
          sx={{
            mt: 0.5,
            flexShrink: 0,
            px: 1,
          }}
        >
          <Tabs
            value={currentTab.split('/').pop()}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-flexContainer': {
                marginTop: '6px',
              },
              '.MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            {roleManagerDetailPages
              .filter((page) => {
                const hasAtLeastEnterpriseTier =
                  usersTier.rank >= gPanelTiers.enterprise.rank;
                const pageIsLimitedVisibility =
                  page.name === pagesConfig.roleManagerLimitedVisibility.name;

                if (
                  // if they aren't on enterprise or they don't have a role with limited visibility already created, don't show the limited visibility tab
                  pageIsLimitedVisibility &&
                  !hasAtLeastEnterpriseTier &&
                  !hasRoleWithLimitedVisibility
                ) {
                  return false;
                }
                return true;
              })
              .map((page) => {
                const doesntHavePermission =
                  !isAbleToSeeUIElement(
                    administrationRoles,
                    page.requiredPermission
                  ) && page.requiredPermission !== '*';
                const doesntHaveTier = usersTier.rank < page.minimumTier;
                const shouldShowButDisabled = isDefault && doesntHaveTier;
                if (shouldShowButDisabled) {
                  return (
                    <Tab
                      key={page.name}
                      value={page.link}
                      component={Link}
                      to={page.link}
                      label={
                        <Tooltip
                          title={Messages.Errors.Tier.Viewing(
                            page.minimumTierDisplayValue
                          )}
                          onClick={(e) => e.preventDefault()}
                          arrow
                        >
                          <span style={{ pointerEvents: 'auto' }}>
                            {page.name}
                          </span>
                        </Tooltip>
                      }
                      icon={<page.icon />}
                      iconPosition="start"
                      sx={{
                        minHeight: '40px',
                        height: '40px',
                        width: 'fit-content',
                      }}
                      disabled
                    />
                  );
                }
                return (
                  !(doesntHavePermission || doesntHaveTier) && (
                    <Tab
                      key={page.name}
                      value={page.link}
                      component={Link}
                      to={page.link}
                      label={page.name}
                      icon={<page.icon />}
                      iconPosition="start"
                      sx={{
                        minHeight: '40px',
                        height: '40px',
                        width: 'fit-content',
                      }}
                    />
                  )
                );
              })}
          </Tabs>
        </Card>

        <RoleDetailsLayout tabsValue={currentTab.split('/').pop()}>
          <Outlet />
        </RoleDetailsLayout>
        {currentRole && openDeleteRole ? (
          <DeleteRoleDialog
            open={openDeleteRole}
            handleClose={() => setOpenDeleteRole(false)}
            selectedRole={currentRole}
            changeLogRoles={changeLogRoles}
            roleNames={roleNames}
          />
        ) : null}
      </Box>
    </SimpleBreadcrumbsPage>
  );
}
